/**
 * Translation job lifecycle — the state between "PDF chosen" and "PDF
 * downloaded", and the ONE gate in front of every provider call.
 *
 *   IDLE
 *   → ANALYZING          file read + layout analysis (browser only)
 *   → SCOPE_PENDING      analysed, no valid scope / quote yet
 *   → QUOTE_READY        a quote is shown; the scope may still change (re-quote)
 *   → QUOTE_CONFIRMED    確認價格: immutable ConfirmedQuote, scope + document locked
 *   → PAYMENT_PENDING    provider.beginPayment() created the order
 *   → PAYMENT_CONFIRMED  provider.verifyPayment() said CONFIRMED, entitlement issued
 *   → TRANSLATING        startTranslation() passed the gate: terminology / translation / QA
 *   → RENDERING          bilingual PDF
 *   → COMPLETED
 *   FAILED               translation / render failure; retry re-enters the gate
 *                        with the same confirmed quote + entitlement
 *
 * disabled payment mode passes PAYMENT_PENDING → PAYMENT_CONFIRMED at once, but
 * every run still goes through both states and through the same gate; nothing
 * jumps from QUOTE_CONFIRMED to TRANSLATING.
 *
 * The lifecycle holds ids, hashes and money only — never PDF text, never a
 * unit. It has no client and makes no request; the payment provider is passed
 * in by the caller.
 */

import { isQuoteValid, type QuoteSnapshot } from '../pricing/quote';
import type { PaymentProvider, PaymentRequest, PaymentResult, TranslationEntitlement } from '../payment/types';

export const JOB_STATUSES = [
  'IDLE',
  'ANALYZING',
  'SCOPE_PENDING',
  'QUOTE_READY',
  'QUOTE_CONFIRMED',
  'PAYMENT_PENDING',
  'PAYMENT_CONFIRMED',
  'TRANSLATING',
  'RENDERING',
  'COMPLETED',
  'FAILED',
] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

/** The immutable record of what the user agreed to. Deep-frozen; the snapshot is a copy. */
export interface ConfirmedQuote {
  readonly quoteId: string;
  readonly pricingVersion: string;
  readonly documentFingerprint: string;
  readonly scopeFingerprint: string;
  readonly billableChars: number;
  readonly billableUnits: number;
  readonly quotedAmount: number;
  readonly expiresAt: string;
  readonly confirmedAt: string;
  /** The whole QuoteSnapshot as it was confirmed (for the post-run validation). */
  readonly snapshot: Readonly<QuoteSnapshot>;
}

/** What the caller knows about the document and scope at the moment of the call. */
export interface JobContext {
  documentFingerprint: string;
  scopeFingerprint: string;
}

export type GateCheckName =
  | 'confirmed_quote'
  | 'quote_not_expired'
  | 'document_fingerprint'
  | 'scope_fingerprint'
  | 'payment_confirmed'
  | 'entitlement_active'
  | 'status';

export interface GateCheck {
  name: GateCheckName;
  ok: boolean;
  detail: string;
}

export interface GateResult {
  pass: boolean;
  checks: GateCheck[];
  /** The first failed check, for messages. */
  failed: GateCheckName | null;
}

export type InvalidationReason = 'document_changed' | 'scope_changed' | 'quote_expired' | 'payment_failed' | 'completed' | 'reset';

export interface JobState {
  status: JobStatus;
  /** The quote currently shown (QUOTE_READY); null otherwise. */
  quote: QuoteSnapshot | null;
  confirmedQuote: ConfirmedQuote | null;
  payment: PaymentResult | null;
  entitlement: TranslationEntitlement | null;
  /** Result of the last gate evaluation, for Developer Mode. */
  lastGate: GateResult | null;
  /** Why the previous confirmed quote was dropped, for Developer Mode. */
  lastInvalidation: InvalidationReason | null;
  /** Message of the last failure (translation / render / payment). */
  failure: string | null;
  /** Number of translation starts of the current confirmed quote (1 = first run, 2+ = retries). */
  translationStarts: number;
}

export class JobStateError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'JobStateError';
  }
}

export type ConfirmResult = { ok: true; confirmed: ConfirmedQuote } | { ok: false; reason: 'no_quote' | 'quote_expired' | 'wrong_status' };
export type StartResult = { ok: true; confirmed: ConfirmedQuote; entitlement: TranslationEntitlement; retry: boolean } | { ok: false; gate: GateResult };

function deepFreeze<T>(value: T): T {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const key of Object.keys(value as object)) deepFreeze((value as Record<string, unknown>)[key]);
  }
  return value;
}

/** Freeze a copy of the snapshot so nothing (a later config change, a scope edit) can alter what was agreed. */
export function confirmQuoteSnapshot(quote: QuoteSnapshot, now = Date.now()): ConfirmedQuote {
  const snapshot = deepFreeze(structuredClone(quote));
  return deepFreeze({
    quoteId: snapshot.quoteId,
    pricingVersion: snapshot.pricingVersion,
    documentFingerprint: snapshot.documentFingerprint,
    scopeFingerprint: snapshot.scopeFingerprint,
    billableChars: snapshot.billableChars,
    billableUnits: snapshot.billableUnits,
    quotedAmount: snapshot.quotedAmount,
    expiresAt: snapshot.expiresAt,
    confirmedAt: new Date(now).toISOString(),
    snapshot,
  });
}

export function paymentRequestFor(confirmed: ConfirmedQuote): PaymentRequest {
  return {
    quoteId: confirmed.quoteId,
    amount: confirmed.quotedAmount,
    documentFingerprint: confirmed.documentFingerprint,
    scopeFingerprint: confirmed.scopeFingerprint,
    quoteExpiresAt: confirmed.expiresAt,
  };
}

function isExpired(iso: string, now: number): boolean {
  return !(Date.parse(iso) > now);
}

/** Statuses from which startTranslation() may be called: the first run, and a retry after a failure. */
const START_ALLOWED: ReadonlySet<JobStatus> = new Set<JobStatus>(['PAYMENT_CONFIRMED', 'FAILED']);

/**
 * The gate. Pure: reads the state and the context, changes nothing.
 * All checks are always evaluated (Developer Mode lists every one of them).
 */
export function evaluateGate(state: JobState, context: JobContext, now = Date.now()): GateResult {
  const cq = state.confirmedQuote;
  const payment = state.payment;
  const ent = state.entitlement;
  const checks: GateCheck[] = [
    { name: 'confirmed_quote', ok: cq !== null, detail: cq ? `已確認報價 ${cq.quoteId}` : '尚未確認報價' },
    {
      name: 'quote_not_expired',
      ok: cq !== null && !isExpired(cq.expiresAt, now),
      detail: cq ? (isExpired(cq.expiresAt, now) ? `報價已於 ${cq.expiresAt} 過期` : `報價有效至 ${cq.expiresAt}`) : '無報價',
    },
    {
      name: 'document_fingerprint',
      ok: cq !== null && cq.documentFingerprint === context.documentFingerprint,
      detail: cq ? `報價 ${cq.documentFingerprint} / 目前 ${context.documentFingerprint}` : '無報價',
    },
    {
      name: 'scope_fingerprint',
      ok: cq !== null && cq.scopeFingerprint === context.scopeFingerprint,
      detail: cq ? `報價 ${cq.scopeFingerprint} / 目前 ${context.scopeFingerprint}` : '無報價',
    },
    {
      name: 'payment_confirmed',
      ok: payment !== null && payment.status === 'CONFIRMED' && cq !== null && payment.quoteId === cq.quoteId,
      detail: payment ? `付款狀態 ${payment.status}（${payment.orderId}）` : '尚未建立付款',
    },
    {
      name: 'entitlement_active',
      ok:
        ent !== null &&
        ent.status === 'ACTIVE' &&
        cq !== null &&
        ent.quoteId === cq.quoteId &&
        ent.documentFingerprint === context.documentFingerprint &&
        ent.scopeFingerprint === context.scopeFingerprint &&
        !isExpired(ent.expiresAt, now),
      detail: ent ? `entitlement ${ent.status}（${ent.issuedBy}，有效至 ${ent.expiresAt}）` : '尚未核發 entitlement',
    },
    { name: 'status', ok: START_ALLOWED.has(state.status), detail: `job 狀態 ${state.status}` },
  ];
  const failed = checks.find((c) => !c.ok);
  return { pass: !failed, checks, failed: failed?.name ?? null };
}

export type JobListener = (state: Readonly<JobState>) => void;

/**
 * One lifecycle per page: reset() when a new file is chosen. All transitions
 * are methods; there is no setter for the status.
 */
export class TranslationJobLifecycle {
  private state: JobState = TranslationJobLifecycle.initial();
  private readonly listeners = new Set<JobListener>();
  private readonly now: () => number;

  constructor(options: { now?: () => number } = {}) {
    this.now = options.now ?? Date.now;
  }

  private static initial(): JobState {
    return {
      status: 'IDLE',
      quote: null,
      confirmedQuote: null,
      payment: null,
      entitlement: null,
      lastGate: null,
      lastInvalidation: null,
      failure: null,
      translationStarts: 0,
    };
  }

  /** Read-only view; every field is a value or an object the lifecycle never mutates in place. */
  get current(): Readonly<JobState> {
    return this.state;
  }

  get status(): JobStatus {
    return this.state.status;
  }

  subscribe(listener: JobListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private set(patch: Partial<JobState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l(this.state);
  }

  // --- document -----------------------------------------------------------

  /** New file chosen (or nothing chosen): everything of the previous document is gone. */
  reset(): void {
    this.state = { ...TranslationJobLifecycle.initial(), lastInvalidation: this.state.confirmedQuote ? 'reset' : null };
    for (const l of this.listeners) l(this.state);
  }

  startAnalysis(): void {
    this.reset();
    this.set({ status: 'ANALYZING' });
  }

  /** Analysis done: waiting for a valid scope (a quote follows via setQuote). */
  analysisDone(): void {
    if (this.state.status !== 'ANALYZING' && this.state.status !== 'IDLE') return;
    this.set({ status: 'SCOPE_PENDING' });
  }

  // --- quote --------------------------------------------------------------

  /**
   * The quote of the scope as the controls stand (null when the scope is not
   * valid). While nothing is confirmed this only replaces the shown quote.
   * With a confirmed quote it is called only after a scope / document change,
   * which invalidates the confirmation first (see observeContext).
   */
  setQuote(quote: QuoteSnapshot | null): void {
    const cq = this.state.confirmedQuote;
    if (cq) {
      if (quote && (quote.documentFingerprint !== cq.documentFingerprint || quote.scopeFingerprint !== cq.scopeFingerprint)) {
        this.invalidate(quote.documentFingerprint !== cq.documentFingerprint ? 'document_changed' : 'scope_changed');
      } else if (quote && this.state.status === 'COMPLETED') {
        this.invalidate('completed'); // the entitlement is spent: the same scope again needs a new quote
      } else {
        return; // confirmed, paid, in flight or failed: the confirmation stands (a retry uses it); nothing to re-quote
      }
    }
    const status: JobStatus = quote ? 'QUOTE_READY' : this.state.status === 'IDLE' || this.state.status === 'ANALYZING' ? this.state.status : 'SCOPE_PENDING';
    this.set({ quote, status });
  }

  /**
   * Document / scope fingerprint protection: whenever the caller's document or
   * scope differs from the confirmed quote, the confirmation, the payment and
   * the entitlement are dropped and the job is back to (re)quoting.
   */
  observeContext(context: JobContext): boolean {
    const cq = this.state.confirmedQuote;
    if (!cq) return false;
    if (cq.documentFingerprint !== context.documentFingerprint) {
      this.invalidate('document_changed');
      return true;
    }
    if (cq.scopeFingerprint !== context.scopeFingerprint) {
      this.invalidate('scope_changed');
      return true;
    }
    return false;
  }

  /** Drop the confirmation, payment and entitlement; back to QUOTE_READY (or SCOPE_PENDING without a quote). */
  invalidate(reason: InvalidationReason): void {
    this.set({
      status: this.state.quote ? 'QUOTE_READY' : 'SCOPE_PENDING',
      confirmedQuote: null,
      payment: null,
      entitlement: null, // REVOKED (scope / document change), EXPIRED (quote expired) or USED (completed): gone either way
      lastGate: null,
      lastInvalidation: reason,
      failure: null,
      translationStarts: 0,
    });
  }

  /** 確認價格: the shown quote becomes the immutable ConfirmedQuote; the scope is locked. */
  confirmQuote(): ConfirmResult {
    const quote = this.state.quote;
    if (this.state.status !== 'QUOTE_READY') return { ok: false, reason: this.state.status === 'SCOPE_PENDING' ? 'no_quote' : 'wrong_status' };
    if (!quote) return { ok: false, reason: 'no_quote' };
    if (!isQuoteValid(quote, this.now())) return { ok: false, reason: 'quote_expired' };
    const confirmed = confirmQuoteSnapshot(quote, this.now());
    this.set({ status: 'QUOTE_CONFIRMED', confirmedQuote: confirmed, payment: null, entitlement: null, failure: null, lastGate: null, translationStarts: 0 });
    return { ok: true, confirmed };
  }

  /** True when the confirmed quote (if any) can no longer be used. */
  confirmedQuoteExpired(): boolean {
    const cq = this.state.confirmedQuote;
    return cq !== null && isExpired(cq.expiresAt, this.now());
  }

  // --- payment ------------------------------------------------------------

  /**
   * QUOTE_CONFIRMED → PAYMENT_PENDING. Idempotent: called again it returns the
   * existing payment (the provider guarantees one order per quoteId too).
   */
  async beginPayment(provider: PaymentProvider): Promise<PaymentResult> {
    const cq = this.state.confirmedQuote;
    if (!cq) throw new JobStateError('beginPayment: no confirmed quote');
    if (this.state.payment) return this.state.payment;
    if (this.state.status !== 'QUOTE_CONFIRMED') throw new JobStateError(`beginPayment: not allowed in ${this.state.status}`);
    if (isExpired(cq.expiresAt, this.now())) {
      this.invalidate('quote_expired');
      throw new QuoteExpiredError();
    }
    const payment = await provider.beginPayment(paymentRequestFor(cq));
    if (payment.quoteId !== cq.quoteId) throw new JobStateError('beginPayment: provider returned a payment for another quote');
    if (payment.status === 'FAILED' || payment.status === 'CANCELLED') {
      this.set({ status: 'FAILED', payment, failure: `payment ${payment.status}` });
      return payment;
    }
    this.set({ status: 'PAYMENT_PENDING', payment });
    return payment;
  }

  /**
   * PAYMENT_PENDING → PAYMENT_CONFIRMED, only when the provider says CONFIRMED.
   * The lifecycle never sets CONFIRMED itself. Idempotent.
   */
  async verifyPayment(provider: PaymentProvider): Promise<PaymentResult> {
    const cq = this.state.confirmedQuote;
    const pending = this.state.payment;
    if (!cq || !pending) throw new JobStateError('verifyPayment: no payment to verify');
    if (pending.status === 'CONFIRMED' && this.state.entitlement) return pending; // already verified: idempotent
    if (pending.status === 'FAILED' || pending.status === 'CANCELLED') return pending; // final: the caller invalidates and re-quotes
    if (this.state.status !== 'PAYMENT_PENDING') throw new JobStateError(`verifyPayment: not allowed in ${this.state.status}`);
    if (isExpired(cq.expiresAt, this.now())) {
      this.invalidate('quote_expired');
      throw new QuoteExpiredError();
    }
    const payment = await provider.verifyPayment(pending.orderId);
    if (payment.orderId !== pending.orderId || payment.quoteId !== cq.quoteId) throw new JobStateError('verifyPayment: provider returned another order');
    if (payment.status !== 'CONFIRMED') {
      this.set({ status: payment.status === 'PENDING' ? 'PAYMENT_PENDING' : 'FAILED', payment, failure: payment.status === 'PENDING' ? null : `payment ${payment.status}` });
      return payment;
    }
    const entitlement = await provider.issueEntitlement(payment, paymentRequestFor(cq));
    this.set({ status: 'PAYMENT_CONFIRMED', payment, entitlement, failure: null });
    return payment;
  }

  // --- translation (the gate) --------------------------------------------

  canStartPaidTranslation(context: JobContext): GateResult {
    const gate = evaluateGate(this.state, context, this.now());
    this.set({ lastGate: gate });
    return gate;
  }

  /**
   * The only way into TRANSLATING. Evaluates every gate check; on failure the
   * job does not move (an expired quote is invalidated so the UI re-quotes).
   */
  startTranslation(context: JobContext): StartResult {
    const gate = this.canStartPaidTranslation(context);
    if (!gate.pass) {
      const expired = gate.checks.find((c) => c.name === 'quote_not_expired');
      if (this.state.confirmedQuote && expired && !expired.ok) {
        this.invalidate('quote_expired'); // the UI re-quotes; a new 確認價格 is required
        this.set({ lastGate: gate });
      }
      return { ok: false, gate };
    }
    const retry = this.state.status === 'FAILED' || this.state.translationStarts > 0;
    this.set({ status: 'TRANSLATING', failure: null, translationStarts: this.state.translationStarts + 1 });
    return { ok: true, confirmed: this.state.confirmedQuote!, entitlement: this.state.entitlement!, retry };
  }

  startRendering(): void {
    if (this.state.status !== 'TRANSLATING') throw new JobStateError(`startRendering: not allowed in ${this.state.status}`);
    this.set({ status: 'RENDERING' });
  }

  /** The PDF is ready: the entitlement is spent. */
  complete(): void {
    if (this.state.status !== 'RENDERING' && this.state.status !== 'TRANSLATING') throw new JobStateError(`complete: not allowed in ${this.state.status}`);
    const ent = this.state.entitlement;
    this.set({ status: 'COMPLETED', entitlement: ent ? { ...ent, status: 'USED' } : null });
  }

  /** Translation / render failure. The confirmed quote, payment and entitlement stay for a retry. */
  fail(message: string): void {
    this.set({ status: 'FAILED', failure: message });
  }
}

export class QuoteExpiredError extends Error {
  constructor() {
    super('quote expired');
    this.name = 'QuoteExpiredError';
  }
}
